/**
 * Display time zone (SPEC C10 extended by the owner 2026-09-27: user-adjustable zone, synced).
 * One helper for every time shown in the UI: resolve the setting to an IANA zone, format in it,
 * label it ("CT", "KST", "ET"), and turn a wall-clock date + time picked in a zone into a UTC
 * instant with a documented DST rule. Self-contained (no imports) so node:test, the UI packages
 * and the chart worker can all load it.
 *
 * DST rule for wall time → instant (the Temporal `"compatible"` disambiguation):
 * - **Gap** (spring forward; the wall time never happens, e.g. 02:30 on 2026-03-08 in Chicago):
 *   interpret it with the offset in force *before* the transition, which lands the same distance
 *   *after* the gap (02:30 → 03:30 CDT). Reported as `kind: "gap"` so the UI can say so.
 * - **Overlap** (fall back; the wall time happens twice, e.g. 01:30 on 2026-11-01 in Chicago):
 *   take the **earlier** instant (01:30 CDT, the first occurrence). Reported as `kind: "overlap"`.
 */

/** The exchange clock (CME Globex runs on Central time). */
export const EXCHANGE_TIME_ZONE = "America/Chicago";

/** Setting values: "local" (browser zone, default), "exchange" (CT), or an IANA zone id. */
export type TimeZoneSetting = "local" | "exchange" | (string & {});

export const DEFAULT_TIME_ZONE_SETTING: TimeZoneSetting = "local";

/** ⚙ How long the browser's zone is reused before it is asked again (a travelling laptop). */
const LOCAL_ZONE_TTL_MS = 60_000;
let localZone: { zone: string; at: number } | null = null;

/**
 * The browser's zone (falls back to UTC where Intl reports none). Cached for a minute: asking Intl
 * builds a formatter, and every row of a live DOM / table resolves the display zone on each
 * render (a CPU profile with a position open had it among the top costs).
 */
export function localTimeZone(nowMs: number = Date.now()): string {
  if (localZone !== null && nowMs >= localZone.at && nowMs - localZone.at < LOCAL_ZONE_TTL_MS) {
    return localZone.zone;
  }
  let zone = "UTC";
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    zone = "UTC";
  }
  localZone = { zone, at: nowMs };
  return zone;
}

const validity = new Map<string, boolean>();

export function isValidTimeZone(zone: string): boolean {
  const hit = validity.get(zone);
  if (hit !== undefined) return hit;
  let ok = false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    ok = zone.length > 0;
  } catch {
    ok = false;
  }
  validity.set(zone, ok);
  return ok;
}

/**
 * Resolve the setting to an IANA zone id. Stable export for the chart engine:
 * `resolveTimeZone(settings.display_time_zone)`. Unknown or invalid values fall back to local.
 */
export function resolveTimeZone(setting: string | null | undefined): string {
  if (setting === null || setting === undefined || setting === "" || setting === "local")
    return localTimeZone();
  if (setting === "exchange") return EXCHANGE_TIME_ZONE;
  return isValidTimeZone(setting) ? setting : localTimeZone();
}

/** Every zone the runtime knows (for the picker), UTC included. */
export function supportedTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: "timeZone") => string[] };
  const list = intl.supportedValuesOf?.("timeZone") ?? [];
  return list.includes("UTC") ? list : ["UTC", ...list];
}

// ---------------------------------------------------------------------------------------------
// Formatting

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string, opts: Intl.DateTimeFormatOptions, locale?: string): Intl.DateTimeFormat {
  const key = `${locale ?? ""}|${zone}|${JSON.stringify(opts)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...opts, timeZone: zone });
    formatters.set(key, f);
  }
  return f;
}

/** `HH:mm:ss` (24 h) in `zone`. */
export function formatTime(ms: number, zone: string, opts: { seconds?: boolean } = {}): string {
  return formatter(zone, {
    hour: "2-digit",
    minute: "2-digit",
    ...(opts.seconds === false ? {} : { second: "2-digit" }),
    hourCycle: "h23",
  }).format(ms);
}

/** e.g. "Sun, Sep 27, 18:30" in `zone` (user locale). */
export function formatDateTime(
  ms: number,
  zone: string,
  opts: { seconds?: boolean; weekday?: boolean } = {},
): string {
  return formatter(zone, {
    ...(opts.weekday === false ? {} : { weekday: "short" }),
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(opts.seconds === true ? { second: "2-digit" } : {}),
    hourCycle: "h23",
  }).format(ms);
}

/** Long date, e.g. "Sunday, Sep 27, 2026", in `zone`. */
export function formatLongDate(ms: number, zone: string): string {
  return formatter(zone, { weekday: "long", month: "short", day: "numeric", year: "numeric" }).format(ms);
}

type Wall = { y: number; mo: number; d: number; h: number; mi: number; s: number };

function wallAt(ms: number, zone: string): Wall {
  const parts = formatter(
    zone,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    },
    "en-US",
  ).formatToParts(ms);
  const get = (t: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === t)?.value ?? "0");
  return {
    y: get("year"),
    mo: get("month"),
    d: get("day"),
    h: get("hour") % 24,
    mi: get("minute"),
    s: get("second"),
  };
}

/** Calendar date of `ms` in `zone` as YYYY-MM-DD (e.g. the journal's trading-day key). */
export function dateKeyInZone(ms: number, zone: string): string {
  const w = wallAt(ms, zone);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${String(w.y)}-${p(w.mo)}-${p(w.d)}`;
}

/** UTC offset of `zone` at `ms`, in ms (Chicago in summer → −5 h). */
export function zoneOffsetMs(ms: number, zone: string): number {
  const w = wallAt(ms, zone);
  const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);
  return asUtc - (ms - (((ms % 1000) + 1000) % 1000));
}

/** Well-known generic abbreviations Intl does not give in en-US (it prints "GMT+9"). */
const ABBREVIATIONS: Readonly<Record<string, string>> = {
  UTC: "UTC",
  "Etc/UTC": "UTC",
  "Etc/GMT": "GMT",
  "America/Chicago": "CT",
  "America/New_York": "ET",
  "America/Denver": "MT",
  "America/Phoenix": "MST",
  "America/Los_Angeles": "PT",
  "America/Anchorage": "AKT",
  "Pacific/Honolulu": "HST",
  "Asia/Seoul": "KST",
  "Asia/Tokyo": "JST",
  "Asia/Hong_Kong": "HKT",
  "Asia/Singapore": "SGT",
  "Asia/Shanghai": "CST (China)",
  "Asia/Taipei": "TST",
  "Asia/Kolkata": "IST",
  "Asia/Dubai": "GST",
  "Australia/Sydney": "AET",
  "Australia/Melbourne": "AET",
  "Australia/Perth": "AWST",
  "Pacific/Auckland": "NZT",
};

/**
 * Short label for a zone: "CT", "ET", "KST", "UTC"… Known zones use a generic (DST-independent)
 * abbreviation; otherwise Intl's short name (en-US, then en-GB for European "BST"/"CEST"); last
 * resort the offset ("GMT+5:30").
 */
export function zoneAbbreviation(zone: string, atMs: number = Date.now()): string {
  const known = ABBREVIATIONS[zone];
  if (known) return known;
  const name = (locale: string, style: "shortGeneric" | "short"): string | null => {
    try {
      const v = formatter(zone, { timeZoneName: style }, locale)
        .formatToParts(atMs)
        .find((p) => p.type === "timeZoneName")?.value;
      return v ?? null;
    } catch {
      return null;
    }
  };
  for (const [locale, style] of [
    ["en-US", "shortGeneric"],
    ["en-US", "short"],
    ["en-GB", "short"],
  ] as const) {
    const v = name(locale, style);
    // Only real abbreviations ("CEST", "IST"): not offsets ("GMT+1") or names ("United Kingdom Time").
    if (v && /^[A-Z]{2,5}$/.test(v)) return v;
  }
  return name("en-US", "short") ?? zone;
}

// ---------------------------------------------------------------------------------------------
// Wall clock in a zone → UTC instant

export type WallTimeResolution =
  { ok: true; ms: number; kind: "exact" | "gap" | "overlap" } | { ok: false; message: string };

const DAY_MS = 86_400_000;

/**
 * `date` "YYYY-MM-DD" + `time` "HH:mm" (or "HH:mm:ss") as a wall clock in `zone` → epoch ms,
 * using the DST rule in the module header (gap → later, overlap → earlier).
 */
export function zonedWallTimeToUtc(date: string, time: string, zone: string): WallTimeResolution {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const tm = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time.trim());
  if (!dm || !tm) return { ok: false, message: "Pick a date and time" };
  const [y, mo, d] = [Number(dm[1]), Number(dm[2]), Number(dm[3])];
  const [h, mi, s] = [Number(tm[1]), Number(tm[2]), Number(tm[3] ?? "0")];
  const wallUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  const check = new Date(wallUtc);
  if (
    Number.isNaN(wallUtc) ||
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() !== mo - 1 ||
    check.getUTCDate() !== d ||
    h > 23 ||
    mi > 59 ||
    s > 59
  ) {
    return { ok: false, message: "That date or time does not exist" };
  }
  if (!isValidTimeZone(zone)) return { ok: false, message: `Unknown time zone ${zone}` };

  const same = (t: number): boolean => {
    const w = wallAt(t, zone);
    return w.y === y && w.mo === mo && w.d === d && w.h === h && w.mi === mi && w.s === s;
  };
  // The offsets in force a day either side cover any single transition near this wall time.
  const before = zoneOffsetMs(wallUtc - DAY_MS, zone);
  const after = zoneOffsetMs(wallUtc + DAY_MS, zone);
  const candidates = [...new Set([before, after, zoneOffsetMs(wallUtc, zone)])]
    .map((o) => wallUtc - o)
    .filter(same)
    .sort((a, b) => a - b);
  const [first] = candidates;
  if (first !== undefined) {
    return { ok: true, ms: first, kind: candidates.length > 1 ? "overlap" : "exact" };
  }
  // Gap: use the pre-transition offset, which lands after the gap.
  return { ok: true, ms: wallUtc - before, kind: "gap" };
}

/**
 * The next instant (≥ `nowMs`) at which the exchange clock reads `hhmm` (e.g. the "17:00" CT
 * trading-day reset). Weekends and holidays are not skipped: this is for the "resets at" label.
 */
export function nextExchangeTime(hhmm: string, nowMs: number): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  for (let dayOffset = 0; dayOffset <= 2; dayOffset++) {
    const key = dateKeyInZone(nowMs + dayOffset * DAY_MS, EXCHANGE_TIME_ZONE);
    const r = zonedWallTimeToUtc(key, hhmm, EXCHANGE_TIME_ZONE);
    if (r.ok && r.ms >= nowMs) return r.ms;
  }
  return null;
}

/**
 * "Daily limits reset 17:00 CT (06:00 KST)" — the parenthetical is dropped when the display zone
 * already is the exchange zone (or shows the same wall time).
 */
export function resetTimeLabel(resetTimeCt: string, displayZone: string, nowMs: number = Date.now()): string {
  const at = nextExchangeTime(resetTimeCt, nowMs);
  const base = `Daily limits reset ${resetTimeCt} CT`;
  if (at === null) return base;
  const local = formatTime(at, displayZone, { seconds: false });
  if (displayZone === EXCHANGE_TIME_ZONE || local === resetTimeCt) return base;
  return `${base} (${local} ${zoneAbbreviation(displayZone, at)})`;
}
